import { GraphQLCampaign as FolderGraphQLCampaign } from 'modules/campaignIndex/domainTypes';
import {
  GraphQLCampaign as EditorGraphQLCampaign,
  RecurrenceScheduleType,
} from 'modules/campaigns/lib/types';
import { EndRecurrenceType } from 'modules/campaigns/editor/types';
import { capitalize, isEqual } from 'lodash';
import { Day, RecurrenceSchedule, SkipDaysOfWeek } from 'modules/domains/campaign';
import { omitDeep } from 'deepdash/standalone';
import { pipe } from 'fp-ts/lib/function';
import { DAYS_OF_THE_WEEK, RECURRENCE_SCHEDULE_MAPPER } from '../constants';

/**
 * Given a campaign, return its `RecurrenceScheduleType`
 */
export const getCampaignRecurrenceScheduleType = (
  campaign: FolderGraphQLCampaign | EditorGraphQLCampaign,
) => {
  const recurSchedule = omitDeep(campaign?.schedulerMsgParams?.recurSchedule, '__typename'); // remove `__typename` so comparison can be one-to-one
  const recurDays = campaign?.schedulerMsgParams?.recurDays;
  /**
   * If a campaign doesn't have `recurSchedule` or `recurDays`,
   * it's currently considered to be a non-recurring campaign
   */
  if (!recurSchedule && !recurDays) return RecurrenceScheduleType.DoNotRepeat;

  const handler = (_type: RecurrenceScheduleType, [type, value]: any[]) => {
    if (value?.period && recurSchedule?.period && value?.period === recurSchedule?.period) {
      /** Note: some `recurSchedule` don't have an `adjustments` field. */
      if (
        isEqual(recurSchedule?.adjustments, (value as unknown as RecurrenceSchedule)?.adjustments)
      )
        return type;
    }

    return _type;
  };

  /**
   * Returns a matched `RecurrenceScheduleType` or `RecurrenceScheduleType.Custom`
   */
  const recurrenceScheduleType = Object.entries(RECURRENCE_SCHEDULE_MAPPER).reduce(
    handler,
    RecurrenceScheduleType.Custom,
  );

  return recurrenceScheduleType;
};

/**
 * Returns whether a user as selected recurring options for scheduling a campaign
 */
export const getIsRecurringScheduleType = (recurrence: RecurrenceScheduleType) =>
  recurrence !== RecurrenceScheduleType.DoNotRepeat;

/**
 * Returns whether a given campaign is a recurring campaign
 */
export const getIsRecurringCampaign = (campaign: FolderGraphQLCampaign | EditorGraphQLCampaign) =>
  pipe(campaign, getCampaignRecurrenceScheduleType, getIsRecurringScheduleType);

/**
 * Returns campaign `EndRecurrenceType`
 */
export const getCampaignEndRecurrenceType = (campaign: EditorGraphQLCampaign) =>
  campaign?.schedulerMsgParams?.endDate?.timestamp
    ? EndRecurrenceType.OnDate
    : EndRecurrenceType.Never;

/**
 * NOTE: some recurrence types don't support `skipOnDays`. ex: `FirstOfTheMonth`
 */
export const getSkipOnDaysText = (skipOnDays: Day[] = [], plural = false) =>
  skipOnDays.reduce((acc, curr, i, arr) => {
    const day = capitalize(plural ? `${curr}s` : curr);
    const comma = skipOnDays.length > 2 ? ',' : '';
    const accumulatedDays = `${acc}${comma} ${i === arr.length - 1 ? 'and ' : ''}${day}`;

    return i === 0 ? day : accumulatedDays;
  }, '');

export const skipDaysMapperToList = (skipDaysOfWeek?: SkipDaysOfWeek) => {
  return Object.entries(skipDaysOfWeek || {}).reduce(
    (acc: Day[], [key, value]: [string, boolean]) => {
      if (DAYS_OF_THE_WEEK.includes(key as Day) && value) {
        acc.push(key as Day);
      }

      return acc;
    },
    [],
  );
};
